import { useEffect, useRef, useState } from "react";
import { api, withToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Upload,
  Trash2,
  Mail,
  Phone,
  Calendar as CalendarIcon,
  LogOut,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";
import ContentEditor from "@/components/ContentEditor";

const TOKEN_KEY = "mirabella_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null
  );
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error("Wrong password. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/admin/logout", null, withToken(token));
    } catch (_) {
      // ignore
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) {
    return (
      <main
        data-testid="admin-login-page"
        className="min-h-screen flex items-center justify-center bg-[#FAF7F2] px-6"
      >
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white p-10 border border-[#EAE0D5] shadow-soft"
        >
          <p className="overline">Hosts area</p>
          <h1 className="font-serif-display text-3xl mt-3 text-[#232E26]">
            Mirabella Anemoessa
          </h1>
          <p className="mt-2 text-[#5B6E4A] text-sm">
            Sign in to manage inquiries and photos.
          </p>
          <div className="mt-8">
            <Label className="overline" htmlFor="pwd">
              Password
            </Label>
            <Input
              id="pwd"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 h-12 bg-white border-[#5B6E4A]/25 focus-visible:ring-[#C65D47]"
              placeholder="••••••••"
              required
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loggingIn}
            className="btn-terra mt-6 w-full px-6 py-4 rounded-sm text-sm tracking-wide uppercase inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loggingIn ? <Loader2 size={16} className="animate-spin" /> : null}
            Sign in
          </button>
          <a
            href="/"
            className="block mt-6 text-center text-sm text-[#5B6E4A] hover:text-[#C65D47]"
          >
            ← Back to website
          </a>
        </form>
      </main>
    );
  }

  return <AdminDashboard token={token} onLogout={handleLogout} />;
}

function AdminDashboard({ token, onLogout }) {
  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#FAF7F2]">
      <div className="border-b border-[#EAE0D5] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <p className="overline">Hosts area</p>
            <h1 className="font-serif-display text-2xl text-[#232E26]">
              Mirabella Anemoessa
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="text-sm text-[#5B6E4A] hover:text-[#C65D47]"
              data-testid="back-to-site"
            >
              View site →
            </a>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 text-sm text-[#232E26] border border-[#5B6E4A]/25 px-3 py-2 rounded-sm hover:bg-[#EAE0D5]/60"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="inquiries">
          <TabsList className="bg-[#EAE0D5]">
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">
              Inquiries
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos">
              Photos
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">
              Website text
            </TabsTrigger>
          </TabsList>
          <TabsContent value="inquiries" className="mt-8">
            <Inquiries token={token} />
          </TabsContent>
          <TabsContent value="photos" className="mt-8">
            <Photos token={token} />
          </TabsContent>
          <TabsContent value="content" className="mt-8">
            <ContentEditor token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

function Inquiries({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/inquiries", withToken(token));
      setItems(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        toast.error("Session expired — please sign in again.");
        window.location.reload();
        return;
      }
      toast.error("Could not load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`, withToken(token));
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Inquiry deleted");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const markRead = async (id) => {
    try {
      await api.patch(
        `/admin/inquiries/${id}`,
        { status: "read" },
        withToken(token)
      );
      setItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, status: "read" } : i))
      );
    } catch (e) {
      toast.error("Could not update");
    }
  };

  if (loading) {
    return (
      <div className="text-[#5B6E4A] flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        data-testid="empty-inquiries"
        className="bg-white border border-[#EAE0D5] p-10 text-center text-[#5B6E4A]"
      >
        No inquiries yet. They will appear here as guests reach out.
      </div>
    );
  }

  return (
    <div data-testid="inquiries-list" className="space-y-4">
      {items.map((it) => (
        <article
          key={it.id}
          data-testid={`inquiry-${it.id}`}
          className="bg-white border border-[#EAE0D5] p-6"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-serif-display text-xl text-[#232E26]">
                {it.name}
              </h3>
              <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#5B6E4A]">
                <span className="flex items-center gap-1">
                  <Mail size={13} /> {it.email}
                </span>
                {it.phone && (
                  <span className="flex items-center gap-1">
                    <Phone size={13} /> {it.phone}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <CalendarIcon size={13} /> {it.checkin} → {it.checkout}
                </span>
                <span>· {it.guests} guests</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant={it.status === "new" ? "default" : "secondary"}
                className={
                  it.status === "new"
                    ? "bg-[#C65D47] text-white"
                    : "bg-[#EAE0D5] text-[#232E26]"
                }
              >
                {it.status}
              </Badge>
              {it.status !== "read" && (
                <Button
                  data-testid={`mark-read-${it.id}`}
                  variant="outline"
                  size="sm"
                  onClick={() => markRead(it.id)}
                >
                  Mark read
                </Button>
              )}
              <Button
                data-testid={`delete-inquiry-${it.id}`}
                variant="outline"
                size="sm"
                onClick={() => remove(it.id)}
                className="text-[#C65D47] border-[#C65D47]/40 hover:bg-[#C65D47] hover:text-white"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
          {it.message && (
            <p className="mt-4 text-[#232E26] whitespace-pre-line leading-relaxed">
              {it.message}
            </p>
          )}
          <p className="mt-4 text-xs text-[#5B6E4A]">
            Received {new Date(it.created_at).toLocaleString()}
          </p>
        </article>
      ))}
    </div>
  );
}

function Photos({ token }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("pool");
  const fileRef = useRef(null);

  const PHOTO_CATEGORIES = [
    { id: "hero", label: "Cover photo (top of site)" },
    { id: "hosts", label: "Hosts portrait (about us)" },
    { id: "pool", label: "Pool & Exterior" },
    { id: "bedrooms", label: "Bedrooms" },
    { id: "bathroom", label: "Bathroom" },
    { id: "living", label: "Living & Kitchen" },
    { id: "balcony", label: "Balcony" },
    { id: "beach", label: "Beach" },
    { id: "town", label: "Town" },
  ];

  const load = async () => {
    try {
      const res = await api.get("/photos");
      setPhotos(res.data || []);
    } catch (e) {
      toast.error("Could not load photos");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is larger than 5MB — please resize.`);
          continue;
        }
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        await api.post(
          "/admin/photos",
          {
            data_url: dataUrl,
            caption: file.name.replace(/\.[^.]+$/, ""),
            category,
          },
          withToken(token)
        );
      }
      toast.success("Photos uploaded");
      await load();
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        toast.error("Session expired — please sign in again.");
        window.location.reload();
        return;
      }
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/admin/photos/${id}`, withToken(token));
      setPhotos((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const grouped = PHOTO_CATEGORIES.map((c) => ({
    ...c,
    items: photos.filter((p) => (p.category || "pool") === c.id),
  }));

  return (
    <div data-testid="photos-manager">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif-display text-2xl text-[#232E26]">
            Property photos
          </h2>
          <p className="text-sm text-[#5B6E4A] mt-1">
            Upload JPG or PNG up to 5MB. Pick a category so guests can browse
            each space.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="overline" htmlFor="upload-category">
              Category
            </Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                id="upload-category"
                data-testid="upload-category"
                className="mt-2 h-11 w-[200px] bg-white border-[#5B6E4A]/25 focus:ring-[#C65D47]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {PHOTO_CATEGORIES.map((c) => (
                  <SelectItem
                    key={c.id}
                    value={c.id}
                    data-testid={`upload-cat-${c.id}`}
                  >
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label
            data-testid="photo-upload-btn"
            className="btn-terra inline-flex items-center gap-2 px-5 py-3 h-11 rounded-sm text-sm cursor-pointer"
          >
            {uploading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Upload size={16} />
            )}
            Upload photos
            <input
              ref={fileRef}
              data-testid="photo-upload-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={onFiles}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {photos.length === 0 ? (
        <div
          data-testid="empty-photos"
          className="bg-white border border-[#EAE0D5] p-10 text-center text-[#5B6E4A]"
        >
          <ImageIcon size={32} className="mx-auto text-[#5B6E4A]/60" />
          <p className="mt-3">
            No photos uploaded yet — the site is showing curated placeholders.
            Pick a category above and upload your own to replace them.
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {grouped.map(
            (g) =>
              g.items.length > 0 && (
                <div key={g.id} data-testid={`photo-group-${g.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif-display text-xl text-[#232E26]">
                      {g.label}
                    </h3>
                    <span className="overline">{g.items.length} photos</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {g.items.map((p) => (
                      <div
                        key={p.id}
                        data-testid={`photo-${p.id}`}
                        className="relative aspect-square overflow-hidden bg-[#EAE0D5] group"
                      >
                        <img
                          src={p.data_url}
                          alt={p.caption || "Property photo"}
                          className="w-full h-full object-cover"
                        />
                        <button
                          data-testid={`delete-photo-${p.id}`}
                          onClick={() => remove(p.id)}
                          className="absolute top-2 right-2 bg-white/95 hover:bg-[#C65D47] hover:text-white text-[#232E26] p-2 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Delete photo"
                        >
                          <Trash2 size={14} />
                        </button>
                        {p.caption && (
                          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-3 text-white text-xs">
                            {p.caption}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
}
